import React, { useEffect, useState } from "react";
import axios from "axios";
import { tryGetProp } from "../utils";
import colors from "../constants/colors";
import { Typography, Backdrop, CircularProgress } from "@material-ui/core";

interface AuthRedirectProps {
	// should show auth redirect message
	isOpen: boolean;
	// action to take when the users cache expires
	tokenCacheNotFoundAction: () => void;
}

/**
 * Sets up a global auth redirect handler and shows the user a message they are
 * about to be redirected to their auth provider page if their token cache has expired
 */
const AuthRedirect: React.FC<AuthRedirectProps> = (props: React.PropsWithChildren<AuthRedirectProps>) => {
	// used to avoid rendering children until setup code in useEffect has completed
	const [setupComplete, setSetupComplete] = useState(false);

	useEffect(() => {
		// setup a global axios interceptor to handle unauthorized requests
		const interceptorId = axios.interceptors.response.use(
			(response) => response,
			(error) => {
				const status = tryGetProp(0, () => error.response.status);
				const powAuthActionHeader = tryGetProp("", () => error.response.headers["pow-auth-action"]);
				if (status === 401 && powAuthActionHeader === "token-cache-not-found") {
					props.tokenCacheNotFoundAction();

					setTimeout(() => {
						window.location.reload();
					}, 3000);

					return;
				}
				return Promise.reject(error);
			},
		);

		setSetupComplete(true);

		return () => axios.interceptors.response.eject(interceptorId);
	}, [props]);

	return (
		<div>
			{props.isOpen ? (
				<Backdrop
					style={{
						zIndex: 99999,
						backgroundColor: colors.gray700,
						color: colors.gray300,
						display: "flex",
						flexDirection: "column",
					}}
					open={props.isOpen}
				>
					<CircularProgress color="inherit" style={{ marginBottom: 16 }} />
					<Typography variant="h6">Authorization token not found</Typography>
					<Typography variant="body1">
						The page will automatically reload and direct you to the login provider.
					</Typography>
				</Backdrop>
			) : (
				setupComplete && props.children
			)}
		</div>
	);
};

export { AuthRedirect };
